import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injector, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { InjectorInstance } from '@app/injector.module';
import { ContentManagerService } from '@app/modules/editor/services/content-manager.service';
import { Client } from '@core/interfaces/client.interface';
import { MetaTagsInfoInterface } from '@core/interfaces/meta-tags-info.interface';
import { MixedObject } from '@core/interfaces/mixed-object';
import { Page } from '@core/interfaces/page.interface';
import { Block } from '@core/models/block/block.model';
import { SpecialBlock } from '@core/models/block/special-block';
import { ApiBlockService } from '@core/services/api/api-block.service';
import { ApiClientService } from '@core/services/client/api-client.service';
import { ApiMetaTagsService } from '@core/services/common/api-meta-tags.service';
import { MetaService } from '@core/services/common/meta.service';
import { CookieService } from '@core/services/cookie/cookie.service';
import { FeatureFlagsService } from '@core/services/features/feature-flags.service';
import { GoogleService } from '@core/services/google/google.service';
import { MessengerService } from '@core/services/messenger/messenger.service';
import { ApiPageService } from '@core/services/page/api-page.service';
import { PageFrameBlocksService } from '@core/services/page/page-frame-blocks.service';
import { PageService } from '@core/services/page/page.service';
import { ClientPageAdapter } from '@shared/services/factories/block-factory.service';
import { SessionStorageService } from '@shared/services/storage';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { perfPhase } from '@core/perf/perf-marks';

let isServer = false;
let injector: Injector;

const isIgnoredUrl = (url: string): boolean => {
  // z jakiegoś powodu SSR próbuje tutaj zaciągnąć czasem pliki mjs.map wiec je odfiltrowałem
  if (url.endsWith('.mjs.map')) {
    return true;
  }

  if (isServer) {
    if (url.startsWith('/assets')) {
      return true;
    }
  }

  return false;
};

export const pageResolver = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  injector = InjectorInstance;
  isServer = isPlatformServer(injector.get(PLATFORM_ID));
  const contentService = inject(ContentManagerService);
  let url = state.url;
  if (url.includes('#')) {
    url = url.split('#')[0];
  }
  if (isIgnoredUrl(url)) {
    return {client: null, page: null};
  }

  if (!isServer) {
    // Warstwa 2 (SPA): flagi macierzy ładowane osobnym strumieniem, tylko w przeglądarce.
    // Izolowane od pobierania strony — błąd flag nie może wywrócić renderu. W SSR flag nie
    // pobieramy (brak TransferState) — zadziałają po hydratacji.
    injector.get(FeatureFlagsService).load().subscribe({error: () => undefined});
  }

  // AT-9296: resolver wyznacza KSZTALT kaskady — trasa nie aktywuje sie, dopoki on nie
  // skonczy, wiec wszystko, co jest tu sekwencyjne, przeklada sie 1:1 na fale zadan.
  const endResolver = perfPhase('resolver-strony');

  return isServer
    ? getMetaTags(url)
    : getClient(url).pipe(
      tap(({page}) => {
        // Przy błędzie pobrania strony nie ma czego zapisywać — resolver ma oddać błąd stronie,
        // a nie wywrócić nawigację.
        if (!page) {
          return;
        }
        contentService.saveMatrixContent(page);
        contentService.getAndSaveBlockContent(page.id);
      }),
      finalize(endResolver),
    );
};

const getMetaTags = (slug: string) => {
  return injector
    .get(ApiMetaTagsService)
    .getMetaTags(slug)
    .pipe(
      tap((metaInfo: MetaTagsInfoInterface) => {
        setTimeout(() => {
          injector.get(MetaService).setGlobalMetaData(metaInfo);
          injector.get(GoogleService).setGoogleScripts(metaInfo);
          injector.get(MessengerService).loadMessenger(metaInfo);
          injector.get(MetaService).setSubPageMetaData(metaInfo);
        }, 500);
      }),
      catchError((err) => {
        console.log('get-client-error', {err});
        return of({error: err});
      }),
    );
};

const getClient = (slug: string, forceHttps = false) => {
  const endClient = perfPhase('resolver-1-klient');

  return injector
    .get(ApiClientService)
    .getClient(forceHttps)
    .pipe(
      finalize(endClient),
      tap((client: Client) => {
        setTimeout(() => {
          injector.get(MetaService).setGlobalMetaData(client.websiteSettings);
          injector.get(GoogleService).setGoogleScripts(client.websiteSettings);
          injector.get(MessengerService).loadMessenger(client.websiteSettings);
        }, 500);
      }),
      mergeMap((client) => joinPageParts(client, slug)),
      catchError((err) => {
        console.log('get-client-error', {err});
        return forceHttps ? of({error: err}) : getClient(slug, true);
      }),
    );
};

const getClientPageRequest = (slug: string): Observable<Page> => {
  // const contentService = inject(ContentManagerService);

  return injector
    .get(ApiPageService)
    .getPage(slug)
    .pipe(
      map((page) => {
        // Pusta odpowiedź po cichu kończyła strumień bez wartości, co anulowało nawigację.
        if (!page) {
          throw new HttpErrorResponse({status: 404, statusText: 'Not Found'});
        }
        return page;
      }),
      tap((page) => {
        injector.get(MetaService).setSubPageMetaData(page);
        injector.get(PageService).page.next(page);
        // console.log(page);
        // contentService.saveMatrixContent(page);
        // contentService.getAndSaveBlockContent(page.id);

        // Po miękkiej nawigacji SPA dociągnij structured_data dla nowej podstrony
        // i wstrzyknij ją w miejsce przestarzałego JSON-LD (np. ze strony głównej z SSR).
        // Niezależna subskrypcja — nie blokuje renderu strony.
        injector
          .get(ApiMetaTagsService)
          .getMetaTags(slug)
          .pipe(catchError(() => of(null)))
          .subscribe((meta) => {
            injector.get(MetaService).setStructuredData(
              (meta as MetaTagsInfoInterface | null)?.structuredData ?? null,
            );
          });
      }),
      // Błąd idzie dalej razem ze statusem — strona inaczej reaguje na brak strony (404),
      // a inaczej na awarię backendu lub sieci.
      catchError((e) => {
        console.log('get-page-error', {e});
        return throwError(() => e);
      }),
    );
};

const joinPageParts = (client: Client, slug: string) => {
  const lang = getLang(slug, client);
  const noCache = getNoCache(slug);

  const endPageAndCookies = perfPhase('resolver-2-strona-i-ciasteczka');

  return forkJoin<[Page, SpecialBlock | null]>([
    getClientPageRequest(slug),
    getSafe(getCookies(lang, noCache)),
  ]).pipe(
    finalize(endPageAndCookies),
    map(([page, cookies]) => {
      // Menu i stopka nie są potrzebne do wyrenderowania treści, więc nie wstrzymują aktywacji
      // trasy. Startują tu, bo zależą od odpowiedzi strony (showMenu, menuBlockId), a strona
      // dokleja je, gdy dojadą.
      const frame = injector.get(PageFrameBlocksService).load(page, client, lang, noCache);

      const blocks = [...page.blocks];
      if (cookies && cookies[0]) {
        blocks.push(cookies[0]);
        injector.get(PageService).saveCookies(cookies[0]);
      }

      return {
        page: injector.get(ClientPageAdapter).adapt({...page, blocks}),
        client,
        frame,
      };
    }),
    catchError((err) => of({client, error: err})),
  );
};

const getCookies = (lang: string, noCache = false): Observable<Block | null> => {
  const cookiesSeen = injector.get(CookieService).isSeen();
  const value = injector.get(PageService).getCookies(lang);
  return !isServer && !cookiesSeen
    ? value
      ? of(value)
      : injector.get(ApiBlockService).getCookies(noCache)
    : of(null);
};

const getSafe = <T>(observable: Observable<T>): Observable<T> => {
  return observable.pipe(catchError(() => of(null)));
};

function getLang(slug: string, client: Client): string {
  if (slug.startsWith('/')) {
    slug = slug.substring(1); //remove first "/"
  }
  if (slug.includes('?')) {
    slug = slug.split('?')[0];
  }
  let lang = slug.split('/')[0];
  if (lang) {
    return lang;
  }

  lang = injector.get(SessionStorageService).getItem('language');
  if (lang) {
    return lang;
  }

  return client.locale;
}

function getNoCache(slug: string): boolean {
  let params = '';
  if (slug.includes('?')) {
    params = slug.split('?')[1];
  }
  if (params.includes('#')) {
    params = params.split('#')[0];
  }
  const obj: MixedObject = {};
  params.split('&').forEach((str) => {
    const [k, v] = str.split('=');
    obj[k.toLowerCase()] = v;
  });

  return Object.prototype.hasOwnProperty.call(obj, 'no-cache');
}
